import type React from 'react'
import { useCallback, useState } from 'react'
import { Modal } from './Modal'
import { Button } from './Button'
import { Input } from './Input'
import { ConfirmModal } from './ConfirmModal'
import { colors, fontSize } from '../../styles/theme'

/**
 * Promise-based confirm / alert / prompt drawn with the app's modals. The browser's alert(), confirm() and
 * prompt() are never used (CLAUDE.md): they block the page, cannot be styled or tested, and break the look.
 *
 *   const dialogs = useDialogs()
 *   if (!(await dialogs.confirm({ title: 'Delete this webhook?', danger: true }))) return
 *   ...
 *   return <>{...page...}{dialogs.element}</>
 */

export interface ConfirmOptions {
  title: string
  message?: React.ReactNode
  confirmLabel?: string
  danger?: boolean
}

export interface AlertOptions {
  title?: string
  message: React.ReactNode
}

export interface PromptOptions {
  title: string
  message?: React.ReactNode
  label: string
  initialValue?: string
  placeholder?: string
  confirmLabel?: string
  /** Keeps OK disabled while the field is blank. */
  required?: boolean
  /** "password" masks the field. */
  type?: 'text' | 'password'
}

type Pending =
  | ({ kind: 'confirm'; resolve: (ok: boolean) => void } & ConfirmOptions)
  | ({ kind: 'alert'; resolve: () => void } & AlertOptions)
  | ({ kind: 'prompt'; resolve: (value: string | null) => void } & PromptOptions)

export function useDialogs() {
  const [pending, setPending] = useState<Pending | null>(null)
  const [text, setText] = useState('')

  const confirm = useCallback((o: ConfirmOptions) =>
    new Promise<boolean>(resolve => setPending({ kind: 'confirm', ...o, resolve })), [])
  const alert = useCallback((o: AlertOptions | string) =>
    new Promise<void>(resolve => setPending({ kind: 'alert', ...(typeof o === 'string' ? { message: o } : o), resolve })), [])
  const prompt = useCallback((o: PromptOptions) => {
    setText(o.initialValue ?? '')
    return new Promise<string | null>(resolve => setPending({ kind: 'prompt', ...o, resolve }))
  }, [])

  let element: React.ReactNode = null
  if (pending?.kind === 'confirm') {
    const done = (ok: boolean) => { setPending(null); pending.resolve(ok) }
    element = (
      <ConfirmModal open title={pending.title} message={pending.message ?? ''} confirmLabel={pending.confirmLabel}
        danger={pending.danger} onConfirm={() => done(true)} onCancel={() => done(false)} />
    )
  } else if (pending?.kind === 'alert') {
    const done = () => { setPending(null); pending.resolve() }
    element = (
      <Modal open onClose={done} title={pending.title ?? 'Something went wrong'} width={420}
        footer={<Button variant="primary" onClick={done}>OK</Button>}>
        <div role="alert" style={{ fontSize: fontSize.base, color: colors.textSecondary, lineHeight: 1.5 }}>{pending.message}</div>
      </Modal>
    )
  } else if (pending?.kind === 'prompt') {
    const done = (value: string | null) => { setPending(null); pending.resolve(value) }
    const blocked = !!pending.required && !text.trim()
    element = (
      <Modal open onClose={() => done(null)} title={pending.title} width={420}
        footer={<>
          <Button variant="ghost" onClick={() => done(null)}>Cancel</Button>
          <Button variant="primary" disabled={blocked} onClick={() => done(text)}>{pending.confirmLabel ?? 'OK'}</Button>
        </>}>
        <form onSubmit={e => { e.preventDefault(); if (!blocked) done(text) }}>
          {pending.message && (
            <div style={{ fontSize: fontSize.base, color: colors.textSecondary, lineHeight: 1.5, marginBottom: 12 }}>{pending.message}</div>
          )}
          <Input label={pending.label} value={text} placeholder={pending.placeholder} autoFocus type={pending.type ?? 'text'}
            onChange={e => setText(e.target.value)} />
        </form>
      </Modal>
    )
  }

  return { confirm, alert, prompt, element }
}
